/**
 * Angebots-Builder (Spec 02, F4) — Positions-Editor auf offer_items.
 *
 * Links Event-Kontext, Mitte Optionen A/B/C mit Positionen nach Sektionen, rechts Live-Summen.
 * Preise werden fuer die Anzeige lokal mit derselben Engine (packages/pricing) berechnet
 * (optimistisch); autoritativ ist der Server: Autosave macht PUT /api/offers/:id/items mit
 * If-Match (Optimistic Locking) und uebernimmt die servergerechneten Summen. Geld immer Cents.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { computeBreakdown, lineGross, type EngineItem } from "@maestro/pricing";
import { useApi, apiMutate } from "../hooks/useApi";
import { apiFetch, toApiError } from "../lib/api";
import { openPrintableDocument } from "../lib/print";
import type {
  CatalogItem,
  Customer,
  EventRow,
  OfferItem,
  OfferOption,
  OfferOptionDay,
  OfferPriceMode,
  OfferSection,
} from "../ui/types";
import { EVENT_STATUS, formatDate, money, parseGermanDecimal, parseGermanEurosToCents } from "../ui/format";
import { Drawer, EmptyState, ErrorNote, Icon, LoadingRows, Modal, PageHeader, StatusPill } from "../ui/components";
// LMIV: Allergene/Diät müssen an JEDER Positions-Anzeige sichtbar sein (rechtlich verpflichtend).
import { AllergenBadges } from "../ui/lmiv";

/* --------------------------------- Vokabular ------------------------------- */
const SECTIONS: { key: OfferSection; label: string; icon: string }[] = [
  { key: "menu", label: "Menü", icon: "restaurant" },
  { key: "drinks", label: "Getränke", icon: "local_bar" },
  { key: "equipment", label: "Equipment", icon: "chair" },
  { key: "staff", label: "Personal", icon: "badge" },
  { key: "other", label: "Sonstiges", icon: "category" },
];
/**
 * Preismodus je Zeile -- steuert, was "Menge" bedeutet (siehe lineGross in packages/pricing):
 *  - per_person: Menge = Portionen PRO GAST, wird automatisch mit der Gästezahl multipliziert.
 *    Normalfall fuer individuelle Gänge (Vorspeise, Hauptgang je Gast) -> Menge bleibt meist 1.
 *  - per_unit: Menge = Anzahl STUECK INSGESAMT (z. B. Platten für mehrere Gäste), OHNE
 *    Gästezahl-Multiplikation. Bei einer Platte für 6 Gäste und 99 Gästen: Menge = 17 (99/6
 *    aufgerundet), Preis = Preis PRO PLATTE (nicht pro Gast).
 *  - flat: Menge wirkungslos, Einzelpreis = Zeilensumme (Pauschale).
 */
const PRICE_MODES: { mode: OfferPriceMode; label: string; hint: string }[] = [
  { mode: "per_person", label: "/Pers.", hint: "Menge = Portionen pro Gast (× automatisch mit Gästezahl multipliziert)" },
  { mode: "per_unit", label: "/Stk.", hint: "Menge = Stück insgesamt (z. B. Platten) — Preis pro Stück, NICHT pro Gast" },
  { mode: "flat", label: "pauschal", hint: "Pauschalpreis — Menge ohne Wirkung" },
];
const VAT_RATES: { bp: number; label: string }[] = [
  { bp: 700, label: "7 %" },
  { bp: 1900, label: "19 %" },
  { bp: 0, label: "0 %" },
];

/** Paket-Zeile von GET /api/packages (siehe pakete.tsx) -- kein `imageUrl`-Feld in echten Daten,
 *  daher Icon-Kachel statt der Foto-Kacheln im Mockup (Design-Abweichung, s. Plan). */
interface Pkg {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  pricePerPersonCents: number;
  vatClass: string;
  active: boolean;
  itemCount?: number;
  // LMIV-Aggregat der Gänge (Allergen-Union + „alle gepflegt?"), siehe /api/packages.
  allergenUnion?: string[];
  allergensResolved?: boolean;
  dietaryAll?: Record<string, boolean>;
  // P2 (Speisekarten): Paket referenziert archivierte Gänge (z. B. nach „Karte ersetzen").
  hasArchivedItems?: boolean;
}
/** Icon je Paket-Art -- gleiches Muster wie KIND_ICON in catalog.tsx. */
const PKG_KIND_ICON: Record<string, string> = { menu: "restaurant_menu", buffet: "dining", flat: "payments" };

/** Katalog-Art -> Angebots-Sektion (dedupliziert die frueher 3x inline wiederholte Zuordnung). */
function kindToSection(kind: CatalogItem["kind"]): OfferSection {
  return kind === "drink" ? "drinks" : kind === "equipment" ? "equipment" : kind === "staff" ? "staff" : kind === "other" ? "other" : "menu";
}

function uuid(): string {
  return crypto.randomUUID();
}
/** Zwei-Buchstaben-Kuerzel fuer den Empfaenger-Avatar im Senden-Dialog. */
function initials(name: string | undefined | null): string {
  if (!name?.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
/** L10: Anzeige der Eingabefelder mit deutschem Komma (12,50) — konsistent mit den
 *  Berechnungszeilen (money()). Rein kosmetisch: das Parsing (parseGermanEurosToCents)
 *  akzeptiert Komma wie Punkt, bleibt also unangetastet. */
function centsToEuros(c: number): string {
  return (c / 100).toFixed(2).replace(".", ",");
}
function milliToQty(m: number): string {
  return (m / 1000).toString().replace(".", ",");
}
function qtyToMilli(v: string): number {
  const n = Number.parseFloat(v.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) : 0;
}
function vatLabel(bp: number): string {
  return VAT_RATES.find((r) => r.bp === bp)?.label ?? `${(bp / 100).toFixed(0)} %`;
}
/** Katalog-Konvention -> Builder-Zeile. */
function catalogToItem(ci: CatalogItem, dayNo: number | null, sortOrder: number, qtyMilli = 1000): OfferItem {
  const section = kindToSection(ci.kind);
  const priceMode: OfferPriceMode = ci.pricingUnit === "per_person" ? "per_person" : ci.pricingUnit === "flat" ? "flat" : "per_unit";
  const vatRateBp = ci.vatClass === "food" ? 700 : 1900;
  return {
    id: uuid(),
    optionId: "",
    dayNo,
    section,
    courseLabel: null,
    name: ci.name,
    description: ci.description,
    catalogItemId: ci.id,
    qtyMilli,
    unit: null,
    unitPriceCents: ci.priceCents,
    catalogPriceCents: ci.priceCents,
    priceMode,
    vatRateBp,
    isOptional: false,
    discountable: true,
    sortOrder,
  };
}
function toEngineItems(items: OfferItem[]): EngineItem[] {
  return items.map((it) => ({
    id: it.id,
    section: it.section,
    priceMode: it.priceMode,
    qtyMilli: it.qtyMilli,
    unitPriceCents: it.unitPriceCents,
    vatRateBp: it.vatRateBp,
    discountable: it.discountable,
    dayNo: it.dayNo,
  }));
}

type SaveState = "idle" | "saving" | "saved" | "conflict" | "error";
/** Ergebnis eines Save-Durchlaufs (H-09): Aufrufer (v.a. der Versand) muessen Konflikt/
 *  Fehler ERKENNEN koennen - ein normal aufloesendes Promise ohne Ergebnis reichte nicht. */
type SaveOutcome = "saved" | "conflict" | "error";

export default function BuilderPage() {
  const { id } = useParams<{ id: string }>();
  const eventQ = useApi<EventRow>(id ? `/api/events/${id}` : null);
  const offersQ = useApi<OfferOption[]>(id ? `/api/events/${id}/offers` : null);
  const event = eventQ.data;
  const customerQ = useApi<Customer>(event ? `/api/customers/${event.customerId}` : null);
  const catalogQ = useApi<CatalogItem[]>("/api/catalog/items");
  // LMIV: Allergene/Diät je Katalog-Position — Positionen mit catalogItemId zeigen die
  // Angaben direkt in der Zeile (Freitext-Positionen haben keine Datenbasis -> „–").
  const lmivByCatalogId = useMemo(
    () => new Map((catalogQ.data ?? []).map((ci) => [ci.id, { allergens: ci.allergens, dietary: ci.dietary }])),
    [catalogQ.data],
  );
  const packagesQ = useApi<Pkg[]>("/api/packages");

  const options = useMemo(
    () => (offersQ.data ?? []).filter((o) => o.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [offersQ.data],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  // H-12: welche Variante ist FERTIG GELADEN (Render-Spiegel von loadedIdRef). Editieren und
  // Speichern sind nur erlaubt, wenn loadedOptionId === activeId - waehrend eines Wechsels
  // oder nach einem fehlgeschlagenen loadFull bleibt der Editor gesperrt.
  const [loadedOptionId, setLoadedOptionId] = useState<string | null>(null);
  const [option, setOption] = useState<OfferOption | null>(null);
  const [items, setItems] = useState<OfferItem[]>([]);
  const [days, setDays] = useState<OfferOptionDay[]>([]);
  const [activeDay, setActiveDay] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);
  // EIN "Position hinzufügen"-Drawer statt drei verstreuter Einstiege (Katalog/Zeile pro
  // Sektion + globaler "Aus Text"-Button) -- Design Turn 8b, siehe Plan (c).
  const [positionDrawerOpen, setPositionDrawerOpen] = useState(false);
  const [positionDrawerSection, setPositionDrawerSection] = useState<OfferSection>("menu");
  const [positionDrawerTab, setPositionDrawerTab] = useState<"katalog" | "ki" | "manuell">("katalog");
  const [catalogSearch, setCatalogSearch] = useState("");
  // P3 (Speisekarten): Karten-Filter im Katalog-Picker; inaktive Karten sind ausgeblendet
  // (die API liefert nur nicht-archivierte, `active` filtern wir hier).
  const [catalogCardFilter, setCatalogCardFilter] = useState<string>("all");
  const builderCardsQ = useApi<{ id: string; name: string; active: boolean }[]>("/api/menu-cards");
  const builderCatsQ = useApi<{ id: string; cardId?: string | null }[]>("/api/catalog/categories");
  const activeBuilderCards = (builderCardsQ.data ?? []).filter((k) => k.active);
  const builderCardByCategory = useMemo(
    () => new Map((builderCatsQ.data ?? []).map((c) => [c.id, c.cardId ?? null])),
    [builderCatsQ.data],
  );
  const [importText, setImportText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<{ count: number; computedTotalCents: number | null; statedTotalCents: number | null; flags: { message: string }[]; warning: string | null } | null>(null);
  // Schnellstart "Paket übernehmen" (8a): Paket-ID waehrend der Anwendung, fuer den Spinner auf der Kachel.
  const [applyingPkgId, setApplyingPkgId] = useState<string | null>(null);
  // M10: In-Flight-Sperre fuer addOption/duplicateOption - ohne sie loest ein Doppelklick zwei
  // POST-Requests aus, bevor offersQ.reload() das Options-Array aktualisiert hat (doppelte Variante).
  const [busyOption, setBusyOption] = useState(false);
  // M3: Variante loeschen -- ID der zu loeschenden Variante fuer den Bestaetigungs-Dialog (destruktiv).
  const [deleteOptionId, setDeleteOptionId] = useState<string | null>(null);
  // L4: zuletzt entfernte Position + urspruenglicher Index fuer den Undo-Toast (kein hartes Modal).
  const [removedItem, setRemovedItem] = useState<{ item: OfferItem; index: number } | null>(null);
  const removedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mobile Akkordeon (8e): welche Sektion ist aufgeklappt (nur < lg relevant, ab lg immer alle offen).
  const [openSection, setOpenSection] = useState<OfferSection>("menu");
  const openSectionInitRef = useRef(false);
  // Einheitlicher Preis-Regler (8d): Modus + roher Live-Vorschau-Text waehrend des Tippens.
  const [discountMode, setDiscountMode] = useState<"percent" | "amount" | "target">("percent");
  const [discountPreviewRaw, setDiscountPreviewRaw] = useState("");

  function openPositionDrawer(section: OfferSection, tab: "katalog" | "ki" | "manuell" = "katalog") {
    setPositionDrawerSection(section);
    setPositionDrawerTab(tab);
    setPositionDrawerOpen(true);
  }
  // Vorschau & Senden (mit KI-Anschreiben) -- 5a (Compose) / 5b (Erfolg) nach Claude-Design-Entwurf.
  const [sendOpen, setSendOpen] = useState(false);
  // H-13: "mail_failed" = Angebot wurde versioniert (Link existiert), aber die E-Mail ging
  // NICHT raus - eigener Zustand mit Retry statt Erfolgsansicht neben einer Fehlermeldung.
  const [sendPhase, setSendPhase] = useState<"compose" | "sent" | "mail_failed">("compose");
  const [sendMailError, setSendMailError] = useState<string | null>(null);
  const [letter, setLetter] = useState("");
  const [letterBusy, setLetterBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [sentResult, setSentResult] = useState<{ publicToken: string; version: number; sentAt: string } | null>(null);
  const [copyDone, setCopyDone] = useState(false);
  const settingsQ = useApi<{ companyEmail: string | null }>("/api/settings/payment-terms");
  const letterHydratedRef = useRef(false);

  // Persistiertes Anschreiben EINMAL beim Laden uebernehmen -- verhindert, dass jedes Oeffnen des
  // Senden-Dialogs erneut die KI aufruft, obwohl schon ein (ggf. bearbeitetes) Anschreiben existiert.
  // Neu generiert wird nur noch explizit ueber "Neu vorschlagen".
  useEffect(() => {
    if (letterHydratedRef.current || !event) return;
    letterHydratedRef.current = true;
    const details = event.details;
    if (details && typeof details === "object" && "offerLetter" in details) {
      const v = (details as Record<string, unknown>).offerLetter;
      if (typeof v === "string" && v.trim()) setLetter(v);
    }
  }, [event]);

  // Refs, damit der debounced Autosave immer den frischen Stand + die frische Version sieht.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const daysRef = useRef(days);
  daysRef.current = days;
  // H-11: Save-Zustand pro Options-ID gekapselt. loadedIdRef sagt, WELCHER Variante die
  // aktuellen items/days-Refs gehoeren (nicht zwingend activeId - beim Wechsel laufen beide
  // kurz auseinander). versionsRef fuehrt die If-Match-Version je Variante: ein nach dem
  // Wechsel nachlaufender Save der alten Variante braucht DEREN Version, nicht die der neuen.
  const loadedIdRef = useRef<string | null>(null);
  const versionsRef = useRef(new Map<string, number>());
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  // H-10: echte Save-Queue statt savingRef/pendingRef-Flags. chainRef ist das Ende der
  // Save-Kette (laufende + eingereihte Saves), queuedRef der bereits eingereihte, noch
  // nicht gestartete Save. save() loest erst auf, wenn der Stand zum Aufrufzeitpunkt
  // serverseitig bestaetigt (oder endgueltig gescheitert) ist - der Flush vor dem Versand
  // ist damit schlicht `await save()`.
  // H-11: der eingereihte Save traegt sein Ziel (target) - Aufrufer fahren nur mit, wenn
  // ihr Ziel dasselbe ist (nach einem Variantenwechsel wird neu eingereiht statt koalesziert).
  const chainRef = useRef<Promise<SaveOutcome>>(Promise.resolve("saved"));
  const queuedRef = useRef<{ target: string | null; promise: Promise<SaveOutcome> } | null>(null);

  // Aktive Option initialisieren.
  useEffect(() => {
    if (options.length === 0) {
      setActiveId(null);
      return;
    }
    if (!activeId || !options.some((o) => o.id === activeId)) setActiveId(options[0]!.id);
  }, [options, activeId]);

  // Volle Daten der aktiven Option laden (Positionen + Tage).
  const loadFull = useCallback(async (optId: string) => {
    setError(null);
    try {
      const res = await apiFetch(`/api/offers/${optId}/full`);
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { option: OfferOption; items: OfferItem[]; days: OfferOptionDay[] } };
      // H-11: Antwort nur uebernehmen, wenn die Variante noch die aktive ist - bei schnellem
      // Doppelwechsel darf eine spaet eintreffende Antwort den State der neueren Variante
      // nicht ueberschreiben.
      if (activeIdRef.current !== optId) return;
      setOption(body.data.option);
      setItems(body.data.items);
      setDays(body.data.days);
      versionsRef.current.set(optId, body.data.option.itemsVersion);
      loadedIdRef.current = optId;
      setLoadedOptionId(optId);
      setActiveDay(body.data.days.length > 0 ? body.data.days[0]!.dayNo : null);
      setSaveState("idle");
      // Mobile Akkordeon (8e): erste Sektion mit Positionen aufklappen (einmalig nach dem
      // ersten Laden -- Wechsel der aktiven Option soll die manuelle Nutzerwahl nicht ueberschreiben).
      if (!openSectionInitRef.current) {
        openSectionInitRef.current = true;
        const firstWithItems = SECTIONS.find((s) => body.data.items.some((it) => it.section === s.key));
        setOpenSection(firstWithItems?.key ?? "menu");
      }
    } catch (e) {
      if (activeIdRef.current === optId) setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  /* --------------------------------- Autosave -------------------------------- */
  /** Fuehrt EINEN Save-Durchlauf fuer targetId aus (liest itemsRef/daysRef erst beim Start).
   *  Wirft nie. H-11: targetId ist die beim EINREIHEN geladene Variante - haelt der State
   *  inzwischen eine andere (Wechsel dazwischen), wird uebersprungen, denn die Refs enthalten
   *  nicht mehr die Daten von targetId (der Wechsel-Effekt flusht vorher). */
  const runSave = useCallback(async (targetId: string | null): Promise<SaveOutcome> => {
    if (!targetId || loadedIdRef.current !== targetId) return "saved";
    setSaveState("saving");
    try {
      const payloadItems = itemsRef.current.map((it, i) => ({
        id: it.id,
        dayNo: it.dayNo,
        section: it.section,
        courseLabel: it.courseLabel,
        name: it.name.trim() || "Position",
        description: it.description,
        catalogItemId: it.catalogItemId,
        qtyMilli: it.qtyMilli,
        unit: it.unit,
        unitPriceCents: it.unitPriceCents,
        catalogPriceCents: it.catalogPriceCents,
        priceMode: it.priceMode,
        vatRateBp: it.vatRateBp,
        isOptional: it.isOptional,
        discountable: it.discountable,
        sortOrder: i,
      }));
      const payloadDays = daysRef.current.map((d) => ({ dayNo: d.dayNo, label: d.label, date: d.date, guestCount: d.guestCount }));
      const res = await apiFetch(`/api/offers/${targetId}/items`, {
        method: "PUT",
        headers: { "If-Match": String(versionsRef.current.get(targetId) ?? 0) },
        body: JSON.stringify({ items: payloadItems, days: payloadDays }),
      });
      if (res.status === 409) {
        if (loadedIdRef.current === targetId) setSaveState("conflict");
        return "conflict";
      }
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { option: OfferOption } };
      versionsRef.current.set(targetId, body.data.option.itemsVersion);
      // H-11: UI-Zustand (option/saveState) nur anfassen, solange die Variante noch geladen
      // ist - eine waehrend des Requests gewechselte Ansicht gehoert einer anderen Variante.
      if (loadedIdRef.current === targetId) {
        setOption(body.data.option);
        setSaveState("saved");
      }
      return "saved";
    } catch (e) {
      if (loadedIdRef.current === targetId) {
        setError(e instanceof Error ? e.message : String(e));
        setSaveState("error");
      }
      return "error";
    }
  }, []);

  /** H-10: reiht einen Save in die Queue ein. Ein bereits EINGEREIHTER (noch nicht
   *  gestarteter) Save liest itemsRef/daysRef erst beim Start und persistiert damit auch
   *  den Stand dieses Aufrufers -> mitfahren statt doppelt einreihen. Laeuft gerade ein
   *  Save, startet der neue erst danach (kein sofort aufloesendes Fake-Promise mehr).
   *  H-11: das Ziel wird beim Einreihen fixiert (die geladene Variante); mitgefahren wird
   *  nur bei GLEICHEM Ziel, sonst wird neu eingereiht. */
  const save = useCallback((): Promise<SaveOutcome> => {
    const targetId = loadedIdRef.current;
    if (queuedRef.current && queuedRef.current.target === targetId) return queuedRef.current.promise;
    const next = chainRef.current.then(() => {
      // Dieser Save startet jetzt: Spaeteres wird NEU eingereiht. Nur den eigenen Eintrag
      // raeumen - ein bereits nachgeruecktes anderes Ziel bleibt eingereiht.
      if (queuedRef.current?.promise === next) queuedRef.current = null;
      return runSave(targetId);
    });
    queuedRef.current = { target: targetId, promise: next };
    chainRef.current = next.catch((): SaveOutcome => "error"); // Kette bleibt defensiv intakt (runSave wirft nie)
    return next;
  }, [runSave]);

  // Variante laden - und vorher ausstehende Saves der NOCH geladenen (alten) Variante
  // abschliessen (H-11): save() ist an loadedIdRef gebunden und die Refs halten bis zum
  // loadFull noch die Daten der alten Variante, der Flush schreibt also garantiert die
  // richtigen Daten an die richtige Options-ID.
  useEffect(() => {
    if (!activeId) return;
    let stale = false;
    void (async () => {
      const previousId = loadedIdRef.current;
      const flushOutcome = previousId && previousId !== activeId ? await save() : "saved";
      if (stale) return;
      // H-12: alten Zustand raeumen, BEVOR die neue Variante laedt - schlaegt loadFull fehl,
      // bleiben keine alten Inhalte unter der neuen ID editierbar (loadedOptionId bleibt null,
      // der Editor gesperrt).
      if (previousId !== activeId) {
        loadedIdRef.current = null;
        setLoadedOptionId(null);
        setOption(null);
        setItems([]);
        setDays([]);
        setSaveState("idle");
      }
      await loadFull(activeId);
      // Nach dem Laden melden (loadFull setzt error zurueck): der Flush der vorigen Variante
      // ist gescheitert - deren letzte Aenderungen sind NICHT gespeichert.
      if (!stale && flushOutcome !== "saved") {
        const prevLabel = options.find((o) => o.id === previousId)?.label;
        setError(
          flushOutcome === "conflict"
            ? `Die letzten Änderungen an Variante ${prevLabel ?? "?"} wurden NICHT gespeichert (Konflikt – anderweitig geändert). Bitte Variante erneut öffnen und prüfen.`
            : `Die letzten Änderungen an Variante ${prevLabel ?? "?"} konnten NICHT gespeichert werden. Bitte Variante erneut öffnen und prüfen.`,
        );
      }
    })();
    return () => {
      stale = true;
    };
    // options bewusst nicht in den Deps: nur fuer das Fehler-Label gelesen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, loadFull, save]);

  /** H-12: Editieren/Speichern nur, wenn die aktive Variante fertig geladen ist. */
  const editable = loadedOptionId != null && loadedOptionId === activeId;

  // Debounced: bei jeder Aenderung (dirty-Zaehler) nach 800 ms speichern.
  const [dirty, setDirty] = useState(0);
  const touch = useCallback(() => setDirty((d) => d + 1), []);
  useEffect(() => {
    if (dirty === 0) return;
    const t = setTimeout(() => void save(), 800);
    return () => clearTimeout(t);
  }, [dirty, save]);

  /* -------------------------------- Mutationen ------------------------------- */
  function patchItem(itemId: string, patch: Partial<OfferItem>) {
    if (!editable) return; // H-12
    setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));
    touch();
  }
  function removeItem(itemId: string) {
    if (!editable) return; // H-12
    // L4: statt hartem Loeschen die Position samt Index merken und einen Undo-Toast zeigen.
    const idx = items.findIndex((it) => it.id === itemId);
    if (idx === -1) return;
    const removed = items[idx]!;
    setItems((prev) => prev.filter((it) => it.id !== itemId));
    setRemovedItem({ item: removed, index: idx });
    if (removedTimerRef.current) clearTimeout(removedTimerRef.current);
    removedTimerRef.current = setTimeout(() => setRemovedItem(null), 6000);
    touch();
  }
  function undoRemove() {
    if (!editable) return; // H-12
    if (!removedItem) return;
    const { item, index } = removedItem;
    setItems((prev) => {
      const next = [...prev];
      next.splice(Math.min(index, next.length), 0, item);
      return next;
    });
    setRemovedItem(null);
    if (removedTimerRef.current) clearTimeout(removedTimerRef.current);
    touch();
  }
  function addFreetext(section: OfferSection) {
    if (!editable) return; // H-12
    setItems((prev) => [
      ...prev,
      { id: uuid(), optionId: activeId ?? "", dayNo: activeDay, section, courseLabel: null, name: "", description: null, catalogItemId: null, qtyMilli: 1000, unit: null, unitPriceCents: 0, catalogPriceCents: null, priceMode: "per_person", vatRateBp: section === "menu" ? 700 : 1900, isOptional: false, discountable: true, sortOrder: prev.length },
    ]);
    touch();
  }
  function addFromCatalog(ci: CatalogItem, qty = 1) {
    if (!editable) return; // H-12
    setItems((prev) => [...prev, { ...catalogToItem(ci, activeDay, prev.length, Math.round(qty * 1000)), optionId: activeId ?? "" }]);
    touch();
  }

  /** Schnellstart (8a): Paket additiv auf die aktive Option anwenden (Backend erzeugt die
   *  Positionen serverseitig aus den Paket-Positionen), danach volle Option neu laden --
   *  gleiches Muster wie schon bei setTargetPrice(). */
  async function applyPackage(pkg: Pkg) {
    if (!activeId || !editable) return; // H-12
    setApplyingPkgId(pkg.id);
    setError(null);
    try {
      await apiMutate(`/api/offers/${activeId}/apply-package/${pkg.id}`, "POST", { dayNo: activeDay });
      await loadFull(activeId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApplyingPkgId(null);
    }
  }

  // KI-Freitext-Import: Text an /parse-freeform, Entwurf als Positionen uebernehmen (kein Auto-Commit).
  async function parseFreeform() {
    if (!id || !importText.trim() || !editable) return; // H-12
    setImportBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/events/${id}/offers/parse-freeform`, { method: "POST", body: JSON.stringify({ text: importText }) });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as {
        data: { items: { name: string; section: OfferSection; priceMode: OfferPriceMode; qtyMilli: number; unit: string | null; unitPriceCents: number; vatRateBp: number; description: string | null }[]; computedTotalCents: number | null; statedTotalCents: number | null; flags: { message: string }[] };
        warning?: string;
      };
      const parsed = body.data.items.map((it, i): OfferItem => ({
        id: uuid(), optionId: activeId ?? "", dayNo: activeDay, section: it.section, courseLabel: null, name: it.name,
        description: it.description, catalogItemId: null, qtyMilli: it.qtyMilli, unit: it.unit, unitPriceCents: it.unitPriceCents,
        catalogPriceCents: null, priceMode: it.priceMode, vatRateBp: it.vatRateBp, isOptional: false, discountable: true, sortOrder: items.length + i,
      }));
      setItems((prev) => [...prev, ...parsed]);
      if (parsed.length > 0) touch();
      setImportResult({ count: parsed.length, computedTotalCents: body.data.computedTotalCents, statedTotalCents: body.data.statedTotalCents, flags: body.data.flags, warning: body.warning ?? null });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImportBusy(false);
    }
  }

  async function addOption() {
    if (!id || busyOption) return;
    setBusyOption(true);
    const label = String.fromCharCode(65 + options.length); // A, B, C …
    try {
      await apiMutate(`/api/events/${id}/offers`, "POST", { label, guestCount: event?.guestCount ?? 1, amountTotalCents: 0 });
      offersQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOption(false);
    }
  }
  async function duplicateOption() {
    if (!activeId || busyOption) return;
    setBusyOption(true);
    try {
      const res = await apiFetch(`/api/offers/${activeId}/duplicate`, { method: "POST" });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { id: string } };
      offersQ.reload();
      setActiveId(body.data.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOption(false);
    }
  }

  /** M3: Variante loeschen -- Soft-Delete via isActive:false (options wird ohnehin auf isActive
   *  gefiltert). Die letzte verbleibende Variante bleibt erhalten (Guard unten + im UI). */
  async function deleteOption(optId: string) {
    if (busyOption || options.length <= 1) return;
    setBusyOption(true);
    setError(null);
    try {
      await apiMutate(`/api/offers/${optId}`, "PATCH", { isActive: false });
      // War die aktive Variante betroffen, waehlt der Init-Effekt nach reload die erste uebrige.
      if (activeId === optId) setActiveId(null);
      offersQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOption(false);
      setDeleteOptionId(null);
    }
  }

  async function setDiscount(percentBp: number | null, amountCents: number | null) {
    if (!activeId || !editable) return; // H-12
    try {
      const res = await apiFetch(`/api/offers/${activeId}/discount`, { method: "POST", body: JSON.stringify({ discountPercentBp: percentBp, discountAmountCents: amountCents }) });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { option: OfferOption } };
      versionsRef.current.set(activeId, body.data.option.itemsVersion);
      setOption(body.data.option);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  async function setTargetPrice(targetCents: number) {
    if (!activeId || !editable) return; // H-12
    try {
      const res = await apiFetch(`/api/offers/${activeId}/set-target-price`, { method: "POST", body: JSON.stringify({ targetCents }) });
      if (!res.ok) throw await toApiError(res);
      await loadFull(activeId); // Anpassungszeile neu einlesen
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function enableDays() {
    if (!editable || days.length > 0) return; // H-12
    const d0: OfferOptionDay = { id: uuid(), optionId: activeId ?? "", dayNo: 0, label: "Tag 1", date: null, guestCount: option?.guestCount ?? event?.guestCount ?? 0 };
    setDays([d0]);
    setActiveDay(0);
    setItems((prev) => prev.map((it) => ({ ...it, dayNo: 0 })));
    touch();
  }
  function addDay() {
    if (!editable) return; // H-12
    const nextNo = days.length === 0 ? 0 : Math.max(...days.map((d) => d.dayNo)) + 1;
    setDays((prev) => [...prev, { id: uuid(), optionId: activeId ?? "", dayNo: nextNo, label: `Tag ${nextNo + 1}`, date: null, guestCount: option?.guestCount ?? 0 }]);
    setActiveDay(nextNo);
    touch();
  }
  function patchDay(dayNo: number, patch: Partial<OfferOptionDay>) {
    if (!editable) return; // H-12
    setDays((prev) => prev.map((d) => (d.dayNo === dayNo ? { ...d, ...patch } : d)));
    touch();
  }
  function removeDay(dayNo: number) {
    if (!editable) return; // H-12
    setDays((prev) => prev.filter((d) => d.dayNo !== dayNo));
    setItems((prev) => prev.map((it) => (it.dayNo === dayNo ? { ...it, dayNo: null } : it)));
    setActiveDay((prev) => (prev === dayNo ? null : prev));
    touch();
  }

/** "15. Juli" -- Tag + langer Monatsname, ohne Jahr (fuer den Betreff-Vorschlag). */
  function dayMonthLong(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("de-DE", { day: "numeric", month: "long" });
  }

  /** Vorschau öffnen und (falls noch keins da) das KI-Anschreiben erzeugen. Ein bereits
   *  gespeichertes Anschreiben (siehe Hydrations-Effekt oben) wird NICHT neu generiert. */
  async function openSend() {
    setError(null);
    setSendOpen(true);
    setSendPhase("compose");
    if (!subject && event) {
      const dm = dayMonthLong(event.eventDate);
      setSubject(event.occasion ? `Ihr Angebot für Ihr ${event.occasion}${dm ? ` am ${dm}` : ""}` : "Ihr Angebot für Ihre Veranstaltung");
    }
    if (!id || letter.trim()) return;
    setLetterBusy(true);
    try {
      const res = await apiMutate<{ letter: string; aiUsed: boolean }>(`/api/events/${id}/offers/generate-letter`, "POST", {});
      setLetter(res.letter);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLetterBusy(false);
    }
  }

  async function copyPublicLink(token: string) {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/angebot/${token}`);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2000);
    } catch {
      /* Zwischenablage nicht verfuegbar -- kein hartes Fehlverhalten noetig. */
    }
  }

  /** Druckfertiges HTML (E-Mail-Vorschau / Angebotsdokument) in neuem Tab oeffnen. */
  async function preview(path: string) {
    try {
      await openPrintableDocument(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** "Gültig bis" direkt aus dem Senden-Dialog pflegen (events.offer_valid_until). */
  async function patchValidUntil(value: string) {
    if (!id) return;
    try {
      await apiMutate(`/api/events/${id}`, "PATCH", { offerValidUntil: value || null });
      eventQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function regenerateLetter() {
    if (!id) return;
    setLetterBusy(true);
    try {
      const res = await apiMutate<{ letter: string }>(`/api/events/${id}/offers/generate-letter`, "POST", {});
      setLetter(res.letter);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLetterBusy(false);
    }
  }

  /** Anschreiben speichern (Edits) und Angebot senden. Bei Erfolg -> 5b (Erfolgs-Ansicht). */
  async function sendOffer() {
    if (!id) return;
    setError(null);
    setSending(true);
    try {
      // M-O: ausstehenden Debounce-Autosave zuerst flushen, sonst geht der letzte Edit
      // (Positionen/Rabatt) beim Versand verloren.
      // H-09: Versand NUR bei bestaetigtem Serverstand. save() faengt Konflikt (409) und
      // Fehler intern - vorher loeste das Promise normal auf und der Kunde bekam die alte
      // Serverfassung, waehrend der Betreiber seine letzten Aenderungen in der UI sah.
      // H-10: save() wartet dank Queue auf saemtliche laufenden UND nachgeschobenen Saves -
      // ein waehrend eines laufenden Saves sofort aufloesendes Promise gibt es nicht mehr.
      const outcome = await save();
      if (outcome !== "saved") {
        setError(
          outcome === "conflict"
            ? "NICHT versendet: Die Variante wurde zwischenzeitlich anderweitig geändert (Konflikt). Bitte neu laden, prüfen und erneut senden."
            : "NICHT versendet: Der letzte Stand konnte nicht gespeichert werden. Bitte Fehler beheben und erneut senden.",
        );
        return;
      }
      if (letter.trim()) {
        // H-13: Anschreiben-Save ist zwingende Vorbedingung. Vorher verschluckte
        // .catch(() => {}) den Fehler und das Angebot ging mit dem ALTEN serverseitigen
        // Anschreiben raus, waehrend der Betreiber seine Edits in der UI sah.
        try {
          await apiMutate(`/api/events/${id}/offer-letter`, "PATCH", { letter });
        } catch (e) {
          setError(
            `NICHT versendet: Das Anschreiben konnte nicht gespeichert werden (${e instanceof Error ? e.message : String(e)}). Bitte erneut versuchen.`,
          );
          return;
        }
      }
      const data = await apiMutate<{ publicToken: string; version: number; emailSent: boolean; emailError: string | null }>(
        `/api/events/${id}/offers/send`,
        "POST",
        { subject: subject.trim() || undefined, ccSelf },
      );
      setSentResult({ publicToken: data.publicToken, version: data.version, sentAt: new Date().toISOString() });
      offersQ.reload();
      if (!data.emailSent) {
        // H-13: NICHT in die Erfolgsphase wechseln - das Angebot ist versioniert und der
        // Link gueltig, aber die Kundin hat KEINE E-Mail bekommen.
        setSendMailError(data.emailError ?? "unbekannter Fehler");
        setSendPhase("mail_failed");
        return;
      }
      setSendMailError(null);
      setSendPhase("sent");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  /* -------------------------------- Ableitungen ------------------------------ */
  const guestCountByDay = useMemo(() => {
    const m: Record<number, number> = {};
    for (const d of days) m[d.dayNo] = d.guestCount;
    return m;
  }, [days]);

  // Lokale, optimistische Summen (Server bestaetigt beim Autosave).
  const breakdown = useMemo(
    () =>
      computeBreakdown({
        items: toEngineItems(items),
        optionGuestCount: option?.guestCount ?? event?.guestCount ?? 0,
        guestCountByDay,
        discountPercentBp: option?.discountPercentBp ?? null,
        discountAmountCents: option?.discountAmountCents ?? null,
      }),
    [items, option, event, guestCountByDay],
  );

  /** Zwischensummen je Sektion fuer die Zusammenfassung (brutto, ohne Rabatt/Anpassung). */
  const sectionSubtotals = useMemo(() => {
    const fallback = option?.guestCount ?? event?.guestCount ?? 0;
    const sums: Partial<Record<OfferSection, number>> = {};
    for (const it of items) {
      if (it.section === "adjustment") continue;
      const guests = it.dayNo != null && it.dayNo in guestCountByDay ? guestCountByDay[it.dayNo]! : fallback;
      const engineItem: EngineItem = { id: it.id, section: it.section, priceMode: it.priceMode, qtyMilli: it.qtyMilli, unitPriceCents: it.unitPriceCents, vatRateBp: it.vatRateBp, discountable: it.discountable };
      sums[it.section] = (sums[it.section] ?? 0) + lineGross(engineItem, guests);
    }
    return SECTIONS.map((s) => ({ key: s.key, label: s.label, cents: sums[s.key] ?? 0 })).filter((s) => s.cents > 0);
  }, [items, option, event, guestCountByDay]);

  const visibleItems = items.filter(
    (it) => it.section !== "adjustment" && (activeDay == null || days.length === 0 ? true : it.dayNo === activeDay),
  );
  const adjustment = items.find((it) => it.section === "adjustment");
  const fallbackGuests = option?.guestCount ?? event?.guestCount ?? 0;
  /** Gaestezahl fuer eine Zeile: Tagesbezug schlaegt Options-Default (spiegelt die Engine-Logik
   *  guestsFor() aus packages/pricing -- fuer die Live-Vorschau in ItemRow). */
  function guestsForItem(it: OfferItem): number {
    if (it.dayNo != null && it.dayNo in guestCountByDay) return guestCountByDay[it.dayNo]!;
    return fallbackGuests;
  }

  /** Katalog-Tab des Positions-Drawers (8c): Suche + auf die gewaehlte Sektion vorgefiltert. */
  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return (catalogQ.data ?? [])
      .filter((ci) => kindToSection(ci.kind) === positionDrawerSection)
      .filter((ci) => {
        // P3: Karten-Filter (Kategorie -> Karte); "none" = Positionen ohne Karten-Zuordnung.
        if (catalogCardFilter === "all") return true;
        const cardId = ci.categoryId ? builderCardByCategory.get(ci.categoryId) ?? null : null;
        return catalogCardFilter === "none" ? cardId == null : cardId === catalogCardFilter;
      })
      .filter((ci) => (q ? ci.name.toLowerCase().includes(q) || (ci.description ?? "").toLowerCase().includes(q) : true));
  }, [catalogQ.data, catalogSearch, positionDrawerSection, catalogCardFilter, builderCardByCategory]);

  /** Live-Vorschau-Zeile fuer den Preis-Regler (8d) -- rein clientseitig, autoritativ bleibt
   *  der Server-Response nach Blur/Submit (setDiscount/setTargetPrice unveraendert). */
  const discountPreviewLine = useMemo(() => {
    const raw = discountPreviewRaw.trim();
    if (!raw) return null;
    const num = Number(raw.replace(",", "."));
    if (!Number.isFinite(num)) return null;
    const subtotal = breakdown.subtotalCents;
    const finalCents =
      discountMode === "percent"
        ? Math.round(subtotal * (1 - Math.max(0, num) / 100))
        : discountMode === "amount"
          ? subtotal - Math.round(num * 100)
          : Math.round(num * 100);
    return `Neu: ${money(finalCents)} (statt ${money(subtotal)})`;
  }, [discountPreviewRaw, discountMode, breakdown.subtotalCents]);

  if (!id) return <ErrorNote message="Kein Event angegeben." />;
  if (eventQ.loading || offersQ.loading) return <LoadingRows rows={6} />;
  if (eventQ.error) return <ErrorNote message={eventQ.error} />;

  return (
    <>
      <PageHeader
        title="Angebot erstellen"
        subtitle={
          event ? (
            <span className="flex flex-wrap items-center gap-2">
              {EVENT_STATUS[event.status] && <StatusPill meta={EVENT_STATUS[event.status]} />}
              <span>
                {customerQ.data?.name ?? "Kunde"} · {event.guestCount ?? "?"} Gäste · {formatDate(event.eventDate)}
                {event.occasion ? ` · ${event.occasion}` : ""}
              </span>
            </span>
          ) : undefined
        }
        actions={
          <>
            {activeId && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => void preview(`/api/offers/${activeId}/document`)}
              >
                <Icon name="visibility" className="text-[18px]" /> Vorschau
              </button>
            )}
            <Link to={`/veranstaltungen/${id}`} className="btn-secondary">
              <Icon name="arrow_back" className="text-[18px]" /> Zurück
            </Link>
          </>
        }
      />

      {error && <ErrorNote message={error} />}

      {options.length === 0 ? (
        <div className="card">
          <EmptyState icon="description" title="Noch keine Angebotsvariante" hint="Legen Sie die erste Variante an." />
          <div className="flex justify-center pb-8">
            <button type="button" className="btn-primary" onClick={() => void addOption()} disabled={busyOption}>
              <Icon name="add" className="text-[18px]" /> Variante anlegen
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[240px_1fr_340px]">
          {/* -------- Kontext -------- */}
          <div className="space-y-4 lg:order-first">
            <div className="card space-y-3 p-4">
              {/* CX-Audit 2026-07-17: Kunde war hier prominente, aber tote Anzeige — jetzt
                  Sprung ins Kundenprofil (Konsistenz mit der Event-Detailseite). */}
              {event?.customerId ? (
                <Link to={`/kunden/${event.customerId}`} className="group flex items-center gap-2.5" title="Kundenprofil öffnen">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/14 font-label-md text-label-md font-bold text-primary">
                    {customerQ.data?.name ? customerQ.data.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase() : "?"}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-body-sm text-body-sm font-semibold text-on-background group-hover:text-primary dark:text-inverse-on-surface">{customerQ.data?.name ?? "Kunde"}</p>
                    <p className="truncate font-body-sm text-[11.5px] text-secondary-fixed-dim">{customerQ.data?.email}</p>
                  </div>
                </Link>
              ) : (
                <div className="flex items-center gap-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/14 font-label-md text-label-md font-bold text-primary">
                    {customerQ.data?.name ? customerQ.data.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase() : "?"}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{customerQ.data?.name ?? "Kunde"}</p>
                    <p className="truncate font-body-sm text-[11.5px] text-secondary-fixed-dim">{customerQ.data?.email}</p>
                  </div>
                </div>
              )}
              <div className="space-y-2 border-t border-outline-variant/30 pt-3 text-body-sm dark:border-outline/30">
                {[
                  { dt: "Anlass", dd: event?.occasion },
                  { dt: "Datum", dd: event ? formatDate(event.eventDate) : null },
                  { dt: "Gäste", dd: event?.guestCount },
                  { dt: "Ort", dd: event?.location },
                  { dt: "Sprache", dd: event?.offerLanguage === "en" ? "English" : event?.offerLanguage === "de" ? "Deutsch" : null },
                ]
                  .filter((r) => r.dd)
                  .map((r) => (
                    <div key={r.dt} className="flex items-center justify-between gap-2">
                      <dt className="text-on-surface-variant/70">{r.dt}</dt>
                      <dd className="truncate font-medium text-on-background dark:text-inverse-on-surface">{r.dd}</dd>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* -------- Editor -------- */}
          <div className="space-y-4">
            {/* Varianten-Vergleich -- mobil horizontal scrollbar (8e), ab lg fixes 3er-Grid (Nicht-anfassen: A/B/C-Vergleich selbst bleibt unveraendert). */}
            <div className="flex gap-2.5 overflow-x-auto pb-1 lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
              {options.map((o) => {
                const isActive = o.id === activeId;
                // M4: die aktive Karte an denselben Live-Recompute binden wie die Zusammenfassung
                // (breakdown), damit €/Gast sofort nach jeder Positions-/Preis-/Mengen-/Rabatt-
                // Aenderung nachzieht. Inaktive Varianten zeigen weiter ihren gespeicherten Stand.
                const totalCents = isActive ? breakdown.amountTotalCents : o.amountTotalCents;
                const guestCount = isActive ? option?.guestCount ?? o.guestCount : o.guestCount;
                const perGuest = guestCount > 0 ? Math.round(totalCents / guestCount) : totalCents;
                return (
                  <div key={o.id} className="group relative w-[160px] shrink-0 lg:w-auto lg:shrink">
                  <button
                    type="button"
                    onClick={() => setActiveId(o.id)}
                    className={`relative block w-full rounded-[11px] p-3 text-left transition-colors ${
                      isActive
                        ? "border-[1.5px] border-primary bg-primary/[0.07]"
                        : "border border-outline-variant/60 bg-surface-container-low hover:bg-surface-container-high/30 dark:border-outline/30 dark:bg-surface-variant/10"
                    }`}
                  >
                    {isActive && (
                      <span className="absolute right-2.5 top-2.5 text-[9.5px] font-semibold uppercase tracking-wide text-primary">
                        Aktiv
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <span
                        className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-[10px] font-bold ${
                          isActive
                            ? "bg-primary text-on-primary"
                            : "bg-surface-container-high text-on-surface-variant dark:bg-surface-variant/50"
                        }`}
                      >
                        {o.label}
                      </span>
                      <span className="truncate text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                        {o.title || `Variante ${o.label}`}
                      </span>
                    </div>
                    <p className={`mt-2 text-[15px] font-bold ${isActive ? "text-primary" : "text-on-background dark:text-inverse-on-surface"}`}>
                      {money(perGuest)} <span className="text-[11px] font-medium text-secondary-fixed-dim">/Gast</span>
                    </p>
                  </button>
                  {/* M3: Loeschen pro Varianten-Karte (Sibling zum Button, kein verschachtelter
                      Button). Nur wenn mehr als eine Variante existiert -- die letzte bleibt erhalten.
                      Erscheint bei Hover/Fokus; Klick oeffnet die Loesch-Bestaetigung. */}
                  {options.length > 1 && (
                    <button
                      type="button"
                      title="Variante löschen"
                      aria-label={`Variante ${o.label} löschen`}
                      onClick={() => setDeleteOptionId(o.id)}
                      disabled={busyOption}
                      className="absolute bottom-2 right-2 grid h-6 w-6 place-items-center rounded-full bg-surface-container-high text-on-surface-variant opacity-0 shadow-sm transition-opacity hover:bg-red-100 hover:text-red-600 focus:opacity-100 focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-40 dark:bg-surface-variant/60 dark:hover:bg-red-500/20 dark:hover:text-red-300"
                    >
                      <Icon name="delete" className="text-[15px]" />
                    </button>
                  )}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="btn-ghost !px-2" title="Variante hinzufügen" onClick={() => void addOption()} disabled={options.length >= 3 || busyOption}>
                <Icon name="add" /> Variante
              </button>
              {activeId && (
                <button type="button" className="btn-ghost !px-2" title="Variante duplizieren" onClick={() => void duplicateOption()} disabled={busyOption}>
                  <Icon name="content_copy" className="text-[18px]" />
                </button>
              )}
              {activeId && (
                <button type="button" className="btn-ghost !px-2 !py-1 text-primary" title="Position hinzufügen" onClick={() => openPositionDrawer(positionDrawerSection, "katalog")}>
                  <Icon name="add_circle" className="text-[16px]" /> Position hinzufügen
                </button>
              )}
              <span className="ml-auto flex items-center gap-1.5 font-label-md text-label-md text-on-surface-variant">
                {saveState === "saving" && (
                  <>
                    <Icon name="sync" className="animate-spin text-[16px]" /> Speichert …
                  </>
                )}
                {saveState === "saved" && (
                  <>
                    <Icon name="cloud_done" className="text-[16px] text-green-600" /> Gespeichert
                  </>
                )}
                {saveState === "conflict" && (
                  <button type="button" className="flex items-center gap-1 text-red-600" onClick={() => activeId && void loadFull(activeId)}>
                    <Icon name="sync_problem" className="text-[16px]" /> Konflikt – neu laden
                  </button>
                )}
                {saveState === "error" && <span className="text-red-600">Nicht gespeichert</span>}
              </span>
            </div>

            {/* H-12: waehrend eines Variantenwechsels oder nach fehlgeschlagenem Laden ist der
                Editor gesperrt - Skeleton statt (potenziell alter) Inhalte unter der neuen ID. */}
            {!editable && (
              <div className="card p-4">
                <LoadingRows rows={4} />
              </div>
            )}

            {/* Titel + Tage */}
            {editable && option && (
              <div className="card space-y-4 p-4">
                <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
                  <label className="block">
                    <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Titel der Variante</span>
                    <input
                      className="input"
                      placeholder={`z. B. „Klassisch" (Variante ${option.label})`}
                      value={option.title ?? ""}
                      onChange={(e) => setOption({ ...option, title: e.target.value })}
                      onBlur={() => activeId && void apiMutate(`/api/offers/${activeId}`, "PATCH", { title: option.title }).catch((e) => setError(e instanceof Error ? e.message : String(e)))}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Gäste</span>
                    <input
                      type="number"
                      className="input"
                      value={option.guestCount}
                      onChange={(e) => setOption({ ...option, guestCount: Math.max(0, Number(e.target.value) || 0) })}
                      onBlur={() => {
                        // N2: anders als die uebrigen Felder hier hing das Promise ohne .catch -
                        // ein fehlgeschlagenes PATCH blieb als unhandled rejection unbemerkt.
                        if (activeId)
                          void apiMutate(`/api/offers/${activeId}`, "PATCH", { guestCount: option.guestCount })
                            .then(() => save())
                            .catch((e) => setError(e instanceof Error ? e.message : String(e)));
                      }}
                    />
                  </label>
                </div>

                {/* Tages-Tabs */}
                {days.length === 0 ? (
                  <button type="button" className="btn-ghost !px-2 text-primary" onClick={enableDays}>
                    <Icon name="calendar_add_on" className="text-[18px]" /> Mehrtägig planen
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {days.map((d) => (
                      <button
                        key={d.dayNo}
                        type="button"
                        onClick={() => setActiveDay(d.dayNo)}
                        className={`rounded-lg px-3 py-1.5 font-label-md text-label-md ${d.dayNo === activeDay ? "bg-secondary-container text-on-secondary-container dark:bg-surface-variant" : "bg-surface-container-high text-on-surface-variant dark:bg-surface-variant/30"}`}
                      >
                        {d.label ?? `Tag ${d.dayNo + 1}`} · {d.guestCount} G.
                      </button>
                    ))}
                    <button type="button" className="btn-ghost !px-2" onClick={addDay} title="Tag hinzufügen">
                      <Icon name="add" className="text-[18px]" />
                    </button>
                  </div>
                )}
                {days.length > 0 && activeDay != null && (
                  <div className="grid gap-3 rounded-lg border border-outline-variant/30 p-3 dark:border-outline/30 sm:grid-cols-[1fr_120px_auto]">
                    <input className="input" placeholder="Tages-Label" value={days.find((d) => d.dayNo === activeDay)?.label ?? ""} onChange={(e) => patchDay(activeDay, { label: e.target.value })} />
                    <input type="number" className="input" value={days.find((d) => d.dayNo === activeDay)?.guestCount ?? 0} onChange={(e) => patchDay(activeDay, { guestCount: Math.max(0, Number(e.target.value) || 0) })} />
                    <button type="button" className="btn-ghost !px-2 text-red-600" onClick={() => removeDay(activeDay)} title="Tag entfernen">
                      <Icon name="delete" className="text-[18px]" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Schnellstart: Paket übernehmen (8a) -- befüllt additiv alle Sektionen der aktiven Option. */}
            {editable && (
              <div className="card space-y-3 p-4">
                <div className="flex items-center gap-2">
                  <Icon name="bolt" className="text-[18px] text-primary" />
                  <span className="font-label-md text-label-md font-semibold text-on-background dark:text-inverse-on-surface">Schnellstart: Paket übernehmen</span>
                </div>
                {packagesQ.loading ? (
                  <LoadingRows rows={1} />
                ) : (packagesQ.data ?? []).filter((p) => p.active).length === 0 ? (
                  <p className="font-body-sm text-body-sm text-on-surface-variant/60">
                    Noch keine Pakete hinterlegt – <Link to="/katalog/pakete" className="text-primary hover:underline">im Katalog anlegen</Link>.
                  </p>
                ) : (
                  <div className="flex gap-2.5 overflow-x-auto pb-1 lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
                    {(packagesQ.data ?? [])
                      .filter((p) => p.active)
                      .map((p) => (
                        <PackageTile key={p.id} pkg={p} busy={applyingPkgId === p.id} onApply={() => void applyPackage(p)} />
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Sektionen -- leere Sektionen schrumpfen zu einer schmalen Zeile (8b); mobil Akkordeon (8e). */}
            {editable && SECTIONS.map(({ key, label, icon }) => {
              const secItems = visibleItems.filter((it) => it.section === key);
              if (secItems.length === 0) {
                return (
                  <div key={key} className="card flex items-center justify-between gap-2 px-4 py-2.5">
                    <span className="flex items-center gap-2 font-label-md text-label-md font-medium text-on-surface-variant dark:text-secondary-fixed-dim">
                      <Icon name={icon} className="text-[18px] text-on-surface-variant/50" /> {label}
                      <span className="text-on-surface-variant/50">— noch keine Positionen</span>
                    </span>
                    <button type="button" className="btn-ghost !px-2 !py-1 shrink-0 text-primary" onClick={() => openPositionDrawer(key, "katalog")}>
                      <Icon name="add" className="text-[16px]" /> Position
                    </button>
                  </div>
                );
              }
              const isOpenMobile = openSection === key;
              return (
                <div key={key} className="card overflow-hidden">
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex w-full cursor-pointer items-center justify-between gap-2 border-b border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-left dark:border-outline/30 dark:bg-surface-variant/10"
                    onClick={() => setOpenSection(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setOpenSection(key);
                    }}
                  >
                    <span className="flex items-center gap-2 font-label-md text-label-md font-semibold text-on-background dark:text-inverse-on-surface">
                      <Icon name={icon} className="text-[18px] text-primary" /> {label}
                      <span className="rounded-full bg-surface-container-high px-1.5 py-0.5 text-[10.5px] text-on-surface-variant dark:bg-surface-variant/30">{secItems.length}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="btn-ghost !px-2 !py-1 text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPositionDrawer(key, "katalog");
                        }}
                      >
                        <Icon name="add" className="text-[16px]" /> Position
                      </button>
                      <Icon name={isOpenMobile ? "expand_less" : "expand_more"} className="text-[18px] text-on-surface-variant/60 lg:hidden" />
                    </span>
                  </div>
                  <div className={`${isOpenMobile ? "" : "hidden"} divide-y divide-outline-variant/20 dark:divide-outline/20 lg:block`}>
                    {secItems.map((it) => (
                      <ItemRow
                        key={it.id}
                        item={it}
                        guests={guestsForItem(it)}
                        lmiv={it.catalogItemId ? lmivByCatalogId.get(it.catalogItemId) : undefined}
                        onPatch={(p) => patchItem(it.id, p)}
                        onRemove={() => removeItem(it.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* -------- Summen-Panel -------- */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:h-fit">
            <div className="card space-y-4 p-5">
              <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Zusammenfassung</h3>
              <dl className="space-y-2 font-body-sm text-body-sm">
                {sectionSubtotals.map((s) => (
                  <Row key={s.key} label={s.label} value={money(s.cents)} muted />
                ))}
                {sectionSubtotals.length > 0 && <div className="my-1 border-t border-outline-variant/20 dark:border-outline/20" />}
                <Row label="Zwischensumme" value={money(breakdown.subtotalCents)} />
                {breakdown.discountCents > 0 && <Row label="Rabatt" value={`− ${money(breakdown.discountCents)}`} tone="green" />}
                {breakdown.adjustmentCents !== 0 && <Row label="Preisanpassung" value={money(breakdown.adjustmentCents)} />}
                <div className="my-2 border-t border-outline-variant/30 dark:border-outline/30" />
                <div className="flex items-center justify-between">
                  <dt className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Gesamt</dt>
                  <dd className="font-headline-sm text-headline-sm font-bold text-primary">{money(breakdown.amountTotalCents)}</dd>
                </div>
                {breakdown.vatBreakdown.map((b) => (
                  <Row key={b.rateBp} label={`enth. USt ${vatLabel(b.rateBp)}`} value={money(b.vatCents)} muted />
                ))}
              </dl>

              <div className="rounded-lg bg-primary/9 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Preis pro Person</span>
                  <span className="font-body-md text-body-md font-bold text-primary">{money(breakdown.perPersonCents)}</span>
                </div>
              </div>

              {option && option.minimumSpendCents != null && breakdown.amountTotalCents < option.minimumSpendCents && (
                <div className="rounded-lg bg-yellow-100 px-3 py-2 font-body-sm text-body-sm text-yellow-800 dark:bg-yellow-400/15 dark:text-yellow-200">
                  Unter Mindestumsatz ({money(option.minimumSpendCents)}).
                </div>
              )}

              {/* Preis anpassen (8d) -- EIN Regler statt drei Feldern: Segmented Rabatt %/€/Zielpreis +
                  Live-Vorschau. Preislogik unveraendert: je Modus weiterhin setDiscount()/setTargetPrice(). */}
              <div className="space-y-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
                <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Preis anpassen</span>
                <div className="flex gap-1 rounded-lg bg-surface-container-high p-1 dark:bg-surface-variant/20">
                  {(
                    [
                      { key: "percent", label: "Rabatt %" },
                      { key: "amount", label: "Rabatt €" },
                      { key: "target", label: "Zielpreis" },
                    ] as const
                  ).map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => {
                        setDiscountMode(m.key);
                        setDiscountPreviewRaw("");
                      }}
                      className={`flex-1 rounded-md px-2 py-1.5 font-label-md text-[11.5px] transition-colors ${
                        discountMode === m.key
                          ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                          : "text-on-surface-variant dark:text-secondary-fixed-dim"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <input
                    // L3: an die GELADENE Option (option?.id) koppeln, nicht an activeId. Beim
                    // Varianten-Wechsel wechselt activeId sofort, waehrend option/breakdown erst
                    // nach loadFull() nachziehen -- ein activeId-Key remountete das Feld dann mit
                    // dem defaultValue der noch alten Variante (Wert-Leak). option?.id aendert sich
                    // erst nach dem Laden, sodass defaultValue immer zur echten Variante passt.
                    key={`${discountMode}-${option?.id ?? activeId}`}
                    className="input pr-8"
                    // M5: Wert bei Fokus markieren, damit Tippen ihn ersetzt statt anzuhaengen.
                    onFocus={(e) => e.target.select()}
                    placeholder={discountMode === "percent" ? "z. B. 10" : discountMode === "amount" ? "z. B. 150,00" : "z. B. 2500,00"}
                    defaultValue={
                      discountMode === "percent"
                        ? option?.discountPercentBp != null
                          ? String(option.discountPercentBp / 100)
                          : ""
                        : discountMode === "amount"
                          ? option?.discountAmountCents != null
                            ? centsToEuros(option.discountAmountCents)
                            : ""
                          : breakdown.discountCents > 0 || breakdown.adjustmentCents !== 0
                            ? centsToEuros(breakdown.amountTotalCents)
                            : ""
                    }
                    onChange={(e) => setDiscountPreviewRaw(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      setDiscountPreviewRaw("");
                      // N-F2: ungültige Eingabe als Validierungsfehler melden statt still auf 0 zu
                      // clampen. M9: negative Prozent-Eingabe weiterhin auf 0 klemmen (kein Aufschlag).
                      if (discountMode === "percent") {
                        if (v === "") return void setDiscount(null, null);
                        const pct = parseGermanDecimal(v);
                        if (pct == null) return setError("Ungültiger Rabatt-Prozentwert.");
                        void setDiscount(Math.round(Math.max(0, pct) * 100), null);
                      } else if (discountMode === "amount") {
                        if (v === "") return void setDiscount(null, null);
                        const cents = parseGermanEurosToCents(v);
                        if (cents == null) return setError("Ungültiger Rabattbetrag.");
                        void setDiscount(null, cents);
                      } else if (v) {
                        const cents = parseGermanEurosToCents(v);
                        if (cents == null) return setError("Ungültiger Zielpreis.");
                        void setTargetPrice(cents);
                      }
                    }}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-body-sm text-on-surface-variant">
                    {discountMode === "percent" ? "%" : "€"}
                  </span>
                </div>
                <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                  {discountPreviewLine ??
                    (discountMode === "target"
                      ? `Unter der Zwischensumme (${money(breakdown.subtotalCents)}) → Rabatt. Darüber → Preisanpassung (Aufschlag).`
                      : "Wert eingeben, dann Enter/Tab zum Übernehmen.")}
                </p>
                {breakdown.discountCents > 0 && !adjustment && (
                  <p className="font-body-sm text-body-sm text-on-surface-variant/70">Als Rabatt hinterlegt: − {money(breakdown.discountCents)}</p>
                )}
                {adjustment && <p className="font-body-sm text-body-sm text-on-surface-variant/70">Anpassung (Aufschlag): {money(adjustment.unitPriceCents)}</p>}
              </div>
            </div>

            {/* Versionen (E8) — gesendete Fassung + aktueller Autosave-Entwurf */}
            <div className="card p-4">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">Versionen</span>
                <span className="ml-auto text-[11px] text-on-surface-variant/70">automatisch gespeichert</span>
              </div>
              <div className="space-y-2">
                {event?.offerSentAt && (
                  <VersionRow badge={`v${event.currentOfferVersion}`} title="Gesendet" sub={formatDate(event.offerSentAt)} />
                )}
                <VersionRow
                  active
                  badge={`v${(event?.currentOfferVersion ?? 0) + (event?.offerSentAt ? 1 : 0) || 1}`}
                  title="Aktueller Entwurf"
                  sub={
                    saveState === "saving"
                      ? "Speichert …"
                      : saveState === "conflict"
                        ? "Konflikt – neu laden"
                        : saveState === "error"
                          ? "Nicht gespeichert"
                          : "Gespeichert"
                  }
                />
              </div>
            </div>

            {/* Aktionen (E9) -- ab lg als Karte im Summen-Panel; mobil ersetzt die fixierte
                Summenleiste unten (8e) den Platz, daher hier ausgeblendet. */}
            <div className="hidden space-y-2 lg:block">
              <button type="button" className="btn-primary w-full" disabled={!editable} onClick={() => void openSend()}>
                <Icon name="send" className="text-[18px]" /> An Kunden senden
              </button>
              <button
                type="button"
                className="btn-secondary w-full"
                disabled={!editable || saveState === "saving"}
                onClick={() => void save()}
              >
                <Icon name="save" className="text-[18px]" /> Als Entwurf speichern
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-on-surface-variant/70">
                <Icon name="lock" className="text-[13px]" /> Erzeugt einen sicheren Link für die Kundin
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: fixierte Summenleiste (8e) -- Gesamtpreis + Senden, ueber der App-Bottom-Nav
          (die bei bottom-0 liegt und ~96px/pb-24 beansprucht, siehe AppShell.tsx). iOS-Safe-Area
          ist damit bereits durch den Versatz abgedeckt (kein weiterer position:fixed-Layer auf
          Hoehe des Home-Indicators). Nur sichtbar, solange eine Option existiert. */}
      {options.length > 0 && editable && option && (
        <div className="fixed inset-x-3 bottom-24 z-30 flex items-center gap-3 rounded-2xl border border-outline-variant/50 bg-surface-container-lowest p-3 shadow-2xl dark:border-outline/30 dark:bg-night-card lg:hidden">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10.5px] uppercase tracking-wide text-secondary-fixed-dim">Gesamt · {option.label}</p>
            <p className="truncate text-[17px] font-bold text-primary">{money(breakdown.amountTotalCents)}</p>
          </div>
          <button type="button" className="btn-secondary !px-3" disabled={saveState === "saving"} onClick={() => void save()} title="Als Entwurf speichern">
            <Icon name="save" className="text-[18px]" />
          </button>
          <button type="button" className="btn-primary shrink-0" onClick={() => void openSend()}>
            <Icon name="send" className="text-[18px]" /> Senden
          </button>
        </div>
      )}
      {/* Platzhalter, damit die fixierte Summenleiste die letzten Karten nicht ueberdeckt. */}
      {options.length > 0 && <div className="h-24 lg:hidden" />}

      {/* Position hinzufügen (8c) -- EIN Drawer mit 3 Reitern (Katalog/KI-Text/Manuell) statt
          der frueheren drei verstreuten Einstiege. Bewusst KEIN 4. Reiter "Pakete": Pakete sind
          Ganz-Angebot-Vorlagen ueber mehrere Sektionen (deckt bereits der Schnellstart in 8a ab),
          kein sektionsgebundenes Konzept -- ein zweites Pakete-Vorkommen hier waere inkonsistent
          mit dem echten Datenmodell (Abweichung vom 4-Tab-Mockup, siehe Plan). */}
      <Drawer
        open={positionDrawerOpen}
        onClose={() => setPositionDrawerOpen(false)}
        title="Position hinzufügen"
        subtitle={`Sektion: ${SECTIONS.find((s) => s.key === positionDrawerSection)?.label ?? ""}`}
        width="480px"
      >
        <div className="space-y-4">
          <div className="flex gap-1 rounded-lg bg-surface-container-high p-1 dark:bg-surface-variant/20">
            {(
              [
                { key: "katalog", label: "Katalog" },
                { key: "ki", label: "KI-Text" },
                { key: "manuell", label: "Manuell" },
              ] as const
            ).map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setPositionDrawerTab(tab.key)}
                className={`flex-1 rounded-md px-3 py-1.5 font-label-md text-label-md transition-colors ${
                  positionDrawerTab === tab.key
                    ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                    : "text-on-surface-variant dark:text-secondary-fixed-dim"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {positionDrawerTab === "katalog" && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {SECTIONS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setPositionDrawerSection(s.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-label-md text-label-md transition-colors ${
                      positionDrawerSection === s.key
                        ? "bg-primary text-on-primary"
                        : "bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest dark:bg-surface-variant/30 dark:text-secondary-fixed-dim"
                    }`}
                  >
                    <Icon name={s.icon} className="text-[14px]" /> {s.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <div className="group relative flex-1">
                  <Icon
                    name="search"
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
                  />
                  <input className="input !pl-10" placeholder="Suchen …" value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} />
                </div>
                {/* P3: nach Karte filtern (nur aktive Karten). */}
                {activeBuilderCards.length > 0 && (
                  <select className="input max-w-[180px]" value={catalogCardFilter} onChange={(e) => setCatalogCardFilter(e.target.value)}>
                    <option value="all">Alle Karten</option>
                    <option value="none">Ohne Karte</option>
                    {activeBuilderCards.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {catalogQ.loading ? (
                <LoadingRows />
              ) : filteredCatalog.length === 0 ? (
                <EmptyState
                  icon="menu_book"
                  title="Nichts gefunden"
                  hint={catalogSearch ? "Suche anpassen." : "Legen Sie zuerst Positionen im Katalog an – oder importieren Sie eine Speisekarte."}
                />
              ) : (
                <div className="space-y-1">
                  {filteredCatalog.map((ci) => (
                    <CatalogPickerRow key={ci.id} item={ci} onAdd={(qty) => addFromCatalog(ci, qty)} />
                  ))}
                </div>
              )}
            </div>
          )}

          {positionDrawerTab === "ki" && (
            <div className="space-y-4">
              <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                Fügen Sie ein altes Angebot oder eine Positionsliste ein. Die KI erstellt einen
                <strong> Entwurf</strong> – nichts wird automatisch gespeichert, Sie prüfen zuerst.
              </p>
              <textarea
                className="input min-h-[160px]"
                placeholder="z. B. „Menü Klassisch 45,00 € pro Person\nGetränkepauschale 25,00 € pro Person\nGesamt 2.800,00 €"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
              {importResult && (
                <div className="rounded-lg border border-outline-variant/30 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
                  {importResult.warning === "nicht_parsebar" ? (
                    <p className="font-body-sm text-body-sm text-yellow-700 dark:text-yellow-300">Kein strukturierter Inhalt erkannt – bitte Text prüfen.</p>
                  ) : (
                    <>
                      <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                        <strong>{importResult.count}</strong> Positionen übernommen · berechnet {money(importResult.computedTotalCents ?? 0)}
                        {importResult.statedTotalCents != null && ` · im Text: ${money(importResult.statedTotalCents)}`}
                      </p>
                      {importResult.flags.map((f, i) => (
                        <p key={i} className="mt-1 flex items-center gap-1.5 font-body-sm text-body-sm text-yellow-700 dark:text-yellow-300">
                          <Icon name="warning" className="text-[14px]" /> {f.message}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button type="button" className="btn-secondary" onClick={() => setPositionDrawerOpen(false)}>
                  {importResult && importResult.count > 0 ? "Fertig" : "Abbrechen"}
                </button>
                <button type="button" className="btn-primary" disabled={importBusy || !importText.trim()} onClick={() => void parseFreeform()}>
                  {importBusy ? "Analysiert …" : "Analysieren & übernehmen"}
                </button>
              </div>
            </div>
          )}

          {positionDrawerTab === "manuell" && (
            <div className="space-y-4">
              <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                Fügt eine leere Position in der Sektion „{SECTIONS.find((s) => s.key === positionDrawerSection)?.label}" hinzu — Bezeichnung, Menge und
                Preis tragen Sie danach direkt in der Zeile ein.
              </p>
              <button
                type="button"
                className="btn-primary w-full"
                onClick={() => {
                  addFreetext(positionDrawerSection);
                  setPositionDrawerOpen(false);
                }}
              >
                <Icon name="add" className="text-[18px]" /> Leere Zeile hinzufügen
              </button>
            </div>
          )}
        </div>
      </Drawer>

      {/* Angebot senden — 5a (Compose, nach Claude-Design-Entwurf) / 5b (Erfolg) im selben Dialog.
          H-13: dritter Zustand "mail_failed" (versioniert, aber E-Mail fehlgeschlagen). */}
      <Modal open={sendOpen} onClose={() => setSendOpen(false)} width={sendPhase === "compose" ? "600px" : "468px"}>
        {sendPhase === "mail_failed" && sentResult ? (
          <>
            <div className="flex flex-col items-center gap-3 px-[26px] pb-1 pt-[30px] text-center">
              <div className="grid h-[60px] w-[60px] place-items-center rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-400/15 dark:text-yellow-300">
                <Icon name="mail_off" className="text-[30px]" />
              </div>
              <h2 className="text-[19px] font-bold tracking-[-0.01em] text-on-background dark:text-inverse-on-surface">
                Angebot gespeichert – E-Mail NICHT versendet
              </h2>
              <p className="text-[12.5px] text-secondary-fixed-dim">
                Das Angebot wurde als Version {sentResult.version} versioniert und der öffentliche Link ist gültig –{" "}
                {customerQ.data?.name ?? "die Kundin"} hat aber <strong>keine E-Mail</strong> erhalten.
              </p>
              <p className="w-full rounded-lg bg-red-500/10 px-3 py-2 text-left font-mono text-[11px] text-red-600 dark:text-red-300">
                {sendMailError ?? "unbekannter Fehler"}
              </p>
            </div>
            <div className="px-[26px] pt-2">
              <div className="flex items-center gap-2 rounded-[9px] border border-outline-variant/40 bg-surface-container-low px-[11px] py-2 dark:border-outline/30 dark:bg-surface-variant/10">
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-on-surface-variant">
                  {window.location.host}/angebot/{sentResult.publicToken}
                </span>
                <button type="button" className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-primary" onClick={() => void copyPublicLink(sentResult.publicToken)}>
                  <Icon name="content_copy" className="text-[13px]" /> {copyDone ? "Kopiert!" : "Kopieren"}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-on-surface-variant/70">
                Sie können den Link manuell an die Kundin senden – oder den E-Mail-Versand erneut versuchen (erzeugt eine neue Version).
              </p>
            </div>
            <footer className="flex items-center gap-2.5 px-[26px] pb-[22px] pt-[18px]">
              <button type="button" className="btn-secondary" onClick={() => setSendOpen(false)} disabled={sending}>
                Schließen
              </button>
              <button type="button" className="btn-primary ml-auto" onClick={() => void sendOffer()} disabled={sending}>
                <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "E-Mail erneut senden"}
              </button>
            </footer>
          </>
        ) : sendPhase === "compose" ? (
          <>
            {/* Header */}
            <header className="flex items-start gap-3 border-b border-outline-variant/30 px-[22px] pb-[15px] pt-[18px] dark:border-outline/30">
              <div className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-[10px] bg-primary/12 text-primary">
                <Icon name="send" className="text-[18px]" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-[16px] font-bold tracking-[-0.01em] text-on-background dark:text-inverse-on-surface">
                  Angebot an {customerQ.data?.name ?? "Kunde"} senden
                </h2>
                <p className="mt-0.5 truncate text-[12px] text-secondary-fixed-dim">
                  {[event?.occasion, event?.eventDate ? formatDate(event.eventDate) : null, event?.guestCount ? `${event.guestCount} Gäste` : null, event?.number ? `Angebot ${event.number}` : null]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <button type="button" className="btn-ghost !h-[30px] !w-[30px] !p-0" onClick={() => setSendOpen(false)} aria-label="Schließen">
                <Icon name="close" className="text-[18px]" />
              </button>
            </header>

            {/* Body */}
            <div className="flex-1 space-y-3.5 overflow-y-auto px-[22px] py-[17px]">
              {/* Empfänger */}
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Empfänger</label>
                <div className="flex items-center gap-2.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest px-[11px] py-2 dark:border-outline/40">
                  <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-primary/14 text-[11px] font-semibold text-primary">
                    {initials(customerQ.data?.name)}
                  </span>
                  <span className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{customerQ.data?.name ?? "Kunde"}</span>
                  <span className="truncate text-[12px] text-secondary-fixed-dim">{customerQ.data?.email ?? "keine E-Mail hinterlegt"}</span>
                </div>
                {settingsQ.data?.companyEmail && (
                  <label className="mt-1.5 flex items-center gap-2 text-[12px] text-on-surface-variant">
                    <input type="checkbox" checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} className="h-4 w-4 rounded accent-primary" />
                    Kopie an mich senden ({settingsQ.data.companyEmail})
                  </label>
                )}
              </div>

              {/* Betreff */}
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Betreff</span>
                <input className="input !py-2.5" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ihr Angebot …" />
              </label>

              {/* Nachricht */}
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <span className="block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Nachricht</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/12 px-2 py-0.5 text-[10.5px] font-semibold text-primary">
                    <Icon name="auto_awesome" className="text-[11px]" /> KI-Anschreiben
                  </span>
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary hover:underline disabled:opacity-50"
                    disabled={letterBusy}
                    onClick={() => void regenerateLetter()}
                  >
                    {letterBusy ? "Erstellt …" : "Neu formulieren"}
                  </button>
                </div>
                {letterBusy && !letter ? (
                  <div className="min-h-[140px] animate-pulse rounded-lg bg-surface-container-high/40 dark:bg-surface-variant/20" />
                ) : (
                  <textarea
                    className="input min-h-[140px] text-[12.5px] leading-relaxed"
                    value={letter}
                    onChange={(e) => setLetter(e.target.value)}
                    placeholder="Sehr geehrte Damen und Herren, …"
                  />
                )}
              </div>

              {/* Live-Vorschau */}
              <div className="space-y-2.5 rounded-xl border border-outline-variant/40 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
                <p className="text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                  Das sieht die Kundin <span className="font-normal text-secondary-fixed-dim">· wählt eine Option online</span>
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {options.map((o) => {
                    const isActive = o.id === activeId;
                    const perGuest = o.guestCount > 0 ? Math.round(o.amountTotalCents / o.guestCount) : o.amountTotalCents;
                    return (
                      <div
                        key={o.id}
                        className={`relative rounded-lg p-2 ${isActive ? "border-[1.5px] border-primary bg-primary/6" : "border border-outline-variant/50 bg-surface-container-lowest dark:border-outline/30"}`}
                      >
                        {isActive && (
                          <span className="absolute -top-2 right-2 rounded-full bg-primary px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-on-primary">
                            Empfohlen
                          </span>
                        )}
                        <p className={`text-[9.5px] font-semibold ${isActive ? "text-primary" : "text-secondary-fixed-dim"}`}>OPTION {o.label}</p>
                        <p className="truncate text-[11.5px] font-semibold text-on-background dark:text-inverse-on-surface">{o.title || `Variante ${o.label}`}</p>
                        <p className={`text-[12.5px] font-bold ${isActive ? "text-primary" : "text-on-background dark:text-inverse-on-surface"}`}>
                          {money(perGuest)} <span className="text-[9.5px] font-medium text-secondary-fixed-dim">/Gast</span>
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 rounded-[9px] border border-outline-variant/40 bg-surface-container-lowest px-[11px] py-2 dark:border-outline/30">
                  <Icon name="public" className="text-[14px] text-primary" />
                  <span className="truncate font-mono text-[11px] text-on-surface-variant">
                    {event?.publicToken ? `${window.location.host}/angebot/${event.publicToken}` : "Link wird beim Senden erzeugt"}
                  </span>
                  <span className="ml-auto shrink-0 text-[11px] text-secondary-fixed-dim">Auswahl & Bestätigung online</span>
                </div>
              </div>

              {/* Anhang + Gültig bis */}
              <div className="flex flex-wrap items-end justify-between gap-3.5">
                <div>
                  <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Anhang</span>
                  <button
                    type="button"
                    className="flex items-center gap-2.5 rounded-[10px] border border-outline-variant/40 bg-surface-container-lowest px-3 py-2 text-left hover:bg-surface-container-high/30 dark:border-outline/30"
                    onClick={() => activeId && void preview(`/api/offers/${activeId}/document`)}
                    disabled={!activeId}
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-surface-container-high text-on-surface-variant dark:bg-surface-variant/30">
                      <Icon name="description" className="text-[18px]" />
                    </span>
                    <span>
                      <span className="block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Angebotsdokument</span>
                      <span className="block text-[10.5px] text-secondary-fixed-dim">Online ansehen (PDF-Druckansicht)</span>
                    </span>
                  </button>
                </div>
                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Gültig bis</span>
                  <input
                    type="date"
                    className="input !py-2"
                    defaultValue={event?.offerValidUntil ?? ""}
                    key={event?.offerValidUntil ?? ""}
                    onBlur={(e) => void patchValidUntil(e.target.value)}
                  />
                </label>
              </div>

              {error && <ErrorNote message={error} />}
            </div>

            {/* Footer */}
            <footer className="flex items-center gap-2.5 border-t border-outline-variant/30 px-[22px] py-3.5 dark:border-outline/30">
              <button type="button" className="btn-secondary" onClick={() => activeId && void preview(`/api/events/${id}/offers/preview-email`)}>
                <Icon name="visibility" className="text-[16px]" /> Vorschau
              </button>
              <div className="ml-auto flex gap-2.5">
                <button type="button" className="btn-secondary" onClick={() => setSendOpen(false)} disabled={sending}>
                  Abbrechen
                </button>
                <button type="button" className="btn-primary" onClick={() => void sendOffer()} disabled={sending || letterBusy}>
                  <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "Jetzt senden"}
                </button>
              </div>
            </footer>
          </>
        ) : (
          sentResult && (
            <>
              <div className="flex flex-col items-center gap-3 px-[26px] pb-1 pt-[30px] text-center">
                <div className="grid h-[60px] w-[60px] place-items-center rounded-full bg-[#E4F1E8] text-[#217A50]">
                  <Icon name="check" className="text-[30px]" />
                </div>
                <h2 className="text-[19px] font-bold tracking-[-0.01em] text-on-background dark:text-inverse-on-surface">Angebot gesendet</h2>
                <p className="text-[12.5px] text-secondary-fixed-dim">
                  {customerQ.data?.name ?? "Die Kundin"} wurde per E-Mail benachrichtigt und kann ihre Wunschoption jetzt online wählen.
                </p>
              </div>
              <div className="space-y-[9px] px-[26px] pt-[18px]">
                <Row label="Empfänger" value={customerQ.data?.name ?? "–"} />
                <Row label="Gesendet" value={new Date(sentResult.sentAt).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
                <Row label="Gültig bis" value={event?.offerValidUntil ? formatDate(event.offerValidUntil) : "–"} />
                <div className="flex items-center justify-between">
                  <dt className="text-on-surface-variant">Status</dt>
                  <dd className="inline-flex items-center gap-1.5 rounded-full bg-[#FAEFD9] px-2.5 py-0.5 text-[11px] font-semibold text-[#96650D]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#C98A1B]" /> Angebot gesendet
                  </dd>
                </div>
              </div>
              <div className="px-[26px] pt-2">
                <div className="flex items-center gap-2 rounded-[9px] border border-outline-variant/40 bg-surface-container-low px-[11px] py-2 dark:border-outline/30 dark:bg-surface-variant/10">
                  <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-on-surface-variant">
                    {window.location.host}/angebot/{sentResult.publicToken}
                  </span>
                  <button type="button" className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-primary" onClick={() => void copyPublicLink(sentResult.publicToken)}>
                    <Icon name="content_copy" className="text-[13px]" /> {copyDone ? "Kopiert!" : "Kopieren"}
                  </button>
                </div>
              </div>
              <footer className="flex flex-col gap-2.5 px-[26px] pb-[22px] pt-[18px]">
                <div className="flex items-center gap-2.5">
                  <button type="button" className="btn-secondary" onClick={() => id && void preview(`/api/events/${id}/offers/preview-email`)}>
                    <Icon name="mail" className="text-[14px]" /> E-Mail ansehen
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => window.open(`${window.location.origin}/angebot/${sentResult.publicToken}`, "_blank")}>
                    <Icon name="public" className="text-[14px]" /> Öffentliche Seite
                  </button>
                </div>
                <div className="flex items-center gap-2.5">
                  {/* F10: zurueck zur Veranstaltung, aus der dieses Angebot erzeugt wurde.
                      CX-Audit 2026-07-17: Label hieß „Zur Anfrage", Ziel ist aber das
                      Event-Detail (/veranstaltungen/:id) — Begriff an das Ziel angeglichen. */}
                  <Link to={`/veranstaltungen/${id}`} className="text-[12.5px] font-semibold text-secondary-fixed-dim hover:underline">
                    Zur Veranstaltung
                  </Link>
                  <button
                    type="button"
                    className="btn-primary ml-auto"
                    onClick={() => {
                      setSendOpen(false);
                      setSendPhase("compose");
                    }}
                  >
                    Fertig
                  </button>
                </div>
              </footer>
            </>
          )
        )}
      </Modal>

      {/* M3: Bestaetigung vor dem Loeschen einer Variante (destruktiv). */}
      <Modal open={deleteOptionId != null} onClose={() => setDeleteOptionId(null)} width="380px">
        <div className="space-y-4 p-6">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300">
              <Icon name="delete" className="text-[20px]" />
            </span>
            <div>
              <h2 className="text-[16px] font-bold text-on-background dark:text-inverse-on-surface">
                Variante {options.find((o) => o.id === deleteOptionId)?.label} löschen?
              </h2>
              <p className="mt-1 text-[12.5px] text-secondary-fixed-dim">
                Die Variante wird aus dem Angebot entfernt. Die zuletzt verbleibende Variante lässt sich nicht löschen.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2.5">
            <button type="button" className="btn-secondary" onClick={() => setDeleteOptionId(null)} disabled={busyOption}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn-primary !bg-red-600 hover:!bg-red-700"
              disabled={busyOption}
              onClick={() => deleteOptionId && void deleteOption(deleteOptionId)}
            >
              <Icon name="delete" className="text-[16px]" /> Löschen
            </button>
          </div>
        </div>
      </Modal>

      {/* L4: Undo-Toast nach dem Entfernen einer Position (leichte Alternative zum Bestaetigungs-
          Modal -- die Position kann sofort wiederhergestellt werden). Ueber der mobilen Summenleiste. */}
      {removedItem && (
        <div className="fixed inset-x-0 bottom-40 z-40 flex justify-center px-4 lg:bottom-6">
          <div className="flex items-center gap-3 rounded-xl border border-outline-variant/50 bg-surface-container-lowest px-4 py-2.5 shadow-2xl dark:border-outline/30 dark:bg-night-card">
            <Icon name="delete" className="text-[16px] text-on-surface-variant" />
            <span className="text-[12.5px] text-on-background dark:text-inverse-on-surface">
              Position „{removedItem.item.name.trim() || "ohne Bezeichnung"}" entfernt
            </span>
            <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline" onClick={undoRemove}>
              <Icon name="undo" className="text-[15px]" /> Rückgängig
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Gepufferte Text-Eingabe fuer Zahlenfelder (Menge/Preis): waehrend des Tippens bleibt der ROH
 * getippte Text erhalten, statt bei jedem Tastendruck kanonisch reformatiert zu werden (z. B.
 * ein gerade getipptes Komma/Dezimalpunkt, das der Parser sofort wieder verschluckt). Ohne diesen
 * Puffer sprang der Cursor beim Tippen am Ende des Felds zurueck bzw. das Feld "reagierte nicht"
 * (Bug: das reformatierte Rendering ueberschrieb den gerade getippten Zwischenzustand). Kanonisch
 * neu formatiert wird erst beim Verlassen des Felds (blur) oder wenn sich der Wert VON AUSSEN
 * aendert (z. B. Server-Reload), waehrend das Feld nicht fokussiert ist.
 */
function useBufferedField(canonical: string, onCommit: (raw: string) => void) {
  const [raw, setRaw] = useState(canonical);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setRaw(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical]);
  return {
    value: raw,
    onFocus: () => {
      focused.current = true;
    },
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      setRaw(e.target.value);
      onCommit(e.target.value);
    },
    onBlur: () => {
      focused.current = false;
      setRaw(canonical);
    },
  };
}

/** Zeigt die Zeilenrechnung transparent an -- macht sichtbar, WAS "Menge" im jeweiligen
 *  Preismodus bedeutet (automatische Gaeste-Multiplikation bei per_person vs. rohe Stueckzahl
 *  bei per_unit vs. wirkungslos bei flat), statt es den Nutzer erraten zu lassen. */
function lineTotalCaption(item: OfferItem, guests: number): string {
  const engineItem: EngineItem = {
    id: item.id,
    section: item.section,
    priceMode: item.priceMode,
    qtyMilli: item.qtyMilli,
    unitPriceCents: item.unitPriceCents,
    vatRateBp: item.vatRateBp,
    discountable: item.discountable,
  };
  const total = money(lineGross(engineItem, guests));
  const qty = milliToQty(item.qtyMilli);
  const price = money(item.unitPriceCents);
  switch (item.priceMode) {
    case "per_person":
      return `${qty} × ${guests} Gäste × ${price} = ${total}`;
    case "per_unit":
      return `${qty} Stk. × ${price} = ${total}`;
    case "flat":
      return `Pauschal (Menge ohne Wirkung) = ${total}`;
  }
}

/* --------------------------------- Zeile ----------------------------------- */
/** Paket-Kachel im Schnellstart (8a): Icon-Tile statt Foto (kein imageUrl im echten Pkg-Modell,
 *  s. Plan-Abweichung), Preis/Gast + Positionsanzahl, Klick uebernimmt additiv in die Option. */
function PackageTile({ pkg, busy, onApply }: { pkg: Pkg; busy: boolean; onApply: () => void }) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onApply}
      className="w-[168px] shrink-0 rounded-[11px] border border-outline-variant/60 bg-surface-container-low p-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/[0.05] disabled:cursor-wait disabled:opacity-60 dark:border-outline/30 dark:bg-surface-variant/10 lg:w-auto lg:shrink"
    >
      <div className="mb-2 grid h-9 w-9 place-items-center rounded-[9px] bg-primary/12 text-primary">
        {busy ? <Icon name="sync" className="animate-spin text-[18px]" /> : <Icon name={PKG_KIND_ICON[pkg.kind] ?? "inventory_2"} className="text-[18px]" />}
      </div>
      <p className="truncate text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">{pkg.name}</p>
      <p className="mt-0.5 text-[13px] font-bold text-primary">
        {money(pkg.pricePerPersonCents)} <span className="text-[10.5px] font-medium text-secondary-fixed-dim">/Gast</span>
      </p>
      <p className="mt-0.5 text-[10.5px] text-secondary-fixed-dim">
        {pkg.itemCount ?? 0} Positionen
        {pkg.hasArchivedItems && (
          <span className="ml-1 rounded bg-amber-500/15 px-1 py-0.5 font-label-md text-[9.5px] font-semibold text-amber-700 dark:text-amber-300" title="Enthält archivierte Positionen (Karte ersetzt?) — Paket prüfen.">
            prüfen
          </span>
        )}
      </p>
      {/* LMIV: Allergen-Union der Gänge schon an der Paket-Kachel sichtbar. */}
      {(pkg.itemCount ?? 0) > 0 && (
        <span className="mt-1 flex flex-wrap items-center gap-1">
          {pkg.allergensResolved === false && (
            <span title="Allergene unvollständig gepflegt (LMIV)">
              <Icon name="warning" className="text-[13px] text-amber-600 dark:text-amber-300" />
            </span>
          )}
          <AllergenBadges allergens={pkg.allergensResolved === false && (pkg.allergenUnion ?? []).length === 0 ? null : (pkg.allergenUnion ?? [])} dietary={pkg.dietaryAll} />
        </span>
      )}
    </button>
  );
}

/** Katalog-Zeile im Position-Drawer (8c): Mengen-Stepper (neu -- existiert sonst nirgends im
 *  Code) um defaultQuantity/minQuantity, dann "Übernehmen" fuegt die Zeile mit dieser Menge hinzu. */
function CatalogPickerRow({ item, onAdd }: { item: CatalogItem; onAdd: (qty: number) => void }) {
  const min = item.minQuantity ?? 0;
  const [qty, setQty] = useState(Math.max(item.defaultQuantity || 1, min || 1));
  return (
    <div className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-surface-container-high/40 dark:hover:bg-surface-variant/20">
      <div className="min-w-0 flex-1">
        <p className="truncate font-body-md text-body-md font-medium text-on-background dark:text-inverse-on-surface">{item.name}</p>
        <p className="flex flex-wrap items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          {item.priceNote ?? money(item.priceCents)}
          {/* LMIV: Allergene schon bei der Auswahl aus dem Katalog sichtbar. */}
          {(item.kind === "food" || item.kind === "drink") && <AllergenBadges allergens={item.allergens} dietary={item.dietary} />}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button type="button" className="btn-ghost !h-7 !w-7 !p-0" title="Weniger" onClick={() => setQty((q) => Math.max(min || 1, q - 1))}>
          <Icon name="remove" className="text-[16px]" />
        </button>
        <span className="w-6 text-center font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{qty}</span>
        <button type="button" className="btn-ghost !h-7 !w-7 !p-0" title="Mehr" onClick={() => setQty((q) => q + 1)}>
          <Icon name="add" className="text-[16px]" />
        </button>
      </div>
      <button type="button" className="btn-primary shrink-0 !px-2.5 !py-1.5" title="Übernehmen" onClick={() => onAdd(qty)}>
        <Icon name="add_circle" className="text-[16px]" />
      </button>
    </div>
  );
}

function ItemRow({
  item,
  guests,
  lmiv,
  onPatch,
  onRemove,
}: {
  item: OfferItem;
  guests: number;
  /** Allergene/Diät der verknüpften Katalog-Position (undefined = Freitext ohne Datenbasis). */
  lmiv?: { allergens: string[] | null; dietary: Record<string, boolean> };
  onPatch: (p: Partial<OfferItem>) => void;
  onRemove: () => void;
}) {
  const cycleMode = () => {
    const idx = PRICE_MODES.findIndex((m) => m.mode === item.priceMode);
    onPatch({ priceMode: PRICE_MODES[(idx + 1) % PRICE_MODES.length]!.mode });
  };
  const cycleVat = () => {
    const idx = VAT_RATES.findIndex((r) => r.bp === item.vatRateBp);
    onPatch({ vatRateBp: VAT_RATES[(idx + 1) % VAT_RATES.length]!.bp });
  };
  const override = item.catalogPriceCents != null && item.catalogPriceCents !== item.unitPriceCents;
  const modeMeta = PRICE_MODES.find((m) => m.mode === item.priceMode)!;
  const qtyField = useBufferedField(milliToQty(item.qtyMilli), (raw) => onPatch({ qtyMilli: qtyToMilli(raw) }));
  // H7: ungültige/mehrdeutige Eingabe (z. B. „1.500") behält den bisherigen Preis statt still
  // auf 0 bzw. 1,50 € zu clampen.
  const priceField = useBufferedField(centsToEuros(item.unitPriceCents), (raw) =>
    onPatch({ unitPriceCents: parseGermanEurosToCents(raw) ?? item.unitPriceCents }),
  );
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2">
        <input className="input flex-1 !py-1.5" placeholder="Bezeichnung" value={item.name} onChange={(e) => onPatch({ name: e.target.value })} />
        <button type="button" className="btn-ghost !px-1.5 text-on-surface-variant" onClick={onRemove} title="Entfernen">
          <Icon name="close" className="text-[16px]" />
        </button>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <input
          className="input !w-16 !py-1 text-right text-body-sm"
          title={modeMeta.hint}
          {...qtyField}
        />
        <span className="text-on-surface-variant/60">×</span>
        <div className="relative">
          <input
            className="input !w-24 !py-1 pr-6 text-right text-body-sm"
            title="Einzelpreis (brutto)"
            {...priceField}
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-body-sm text-on-surface-variant">€</span>
        </div>
        <button type="button" onClick={cycleMode} className="rounded border border-outline-variant/50 px-2 py-1 font-label-md text-[11px] text-on-surface-variant hover:border-primary/40 dark:border-outline/40" title={modeMeta.hint}>
          {modeMeta.label}
        </button>
        <button type="button" onClick={cycleVat} className="rounded border border-outline-variant/50 px-2 py-1 font-label-md text-[11px] text-on-surface-variant hover:border-primary/40 dark:border-outline/40" title="MwSt">
          {vatLabel(item.vatRateBp)}
        </button>
        {override && (
          <span className="rounded bg-yellow-100 px-1.5 py-0.5 font-label-md text-[10px] text-yellow-800 dark:bg-yellow-400/15 dark:text-yellow-200" title={`Katalogpreis ${money(item.catalogPriceCents ?? 0)}`}>
            geändert
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="font-body-sm text-[11px] text-on-surface-variant/60">{lineTotalCaption(item, guests)}</p>
        {/* LMIV: Allergene/Diät je Position — bei Speisen/Getränken IMMER sichtbar, auch der
            ungepflegte Zustand („–"), damit fehlende Pflichtangaben nie stumm untergehen. */}
        {(item.section === "menu" || item.section === "drinks") && (
          <span className="inline-flex items-center gap-1">
            <span className="font-label-md text-[10px] uppercase tracking-wider text-on-surface-variant/50">Allergene</span>
            <AllergenBadges allergens={lmiv ? lmiv.allergens : null} dietary={lmiv?.dietary} />
          </span>
        )}
      </div>
    </div>
  );
}

/** Zeile in der Versionen-Karte (Design 2d): Badge v-Nummer + Titel + Zeitpunkt/Status. */
function VersionRow({ badge, title, sub, active }: { badge: string; title: string; sub: string; active?: boolean }) {
  return (
    <div
      className={`flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 ${
        active
          ? "border-[1.5px] border-primary bg-primary/[0.07]"
          : "border border-outline-variant/50 bg-surface-container-low dark:border-outline/30 dark:bg-surface-variant/10"
      }`}
    >
      <span
        className={`grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md text-[10.5px] font-bold ${
          active ? "bg-primary text-on-primary" : "bg-surface-container-high text-on-surface-variant dark:bg-surface-variant/50"
        }`}
      >
        {badge}
      </span>
      <div className="min-w-0 flex-1">
        <div className={`truncate text-[12px] ${active ? "font-semibold" : "font-medium"} text-on-background dark:text-inverse-on-surface`}>
          {title}
        </div>
        <div className="truncate text-[10.5px] text-on-surface-variant/70">{sub}</div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, tone }: { label: string; value: string; muted?: boolean; tone?: "green" }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={muted ? "text-on-surface-variant/70" : "text-on-surface-variant dark:text-secondary-fixed-dim"}>{label}</dt>
      <dd className={`font-medium ${tone === "green" ? "text-green-700 dark:text-green-300" : "text-on-background dark:text-inverse-on-surface"} ${muted ? "!font-normal text-on-surface-variant/70" : ""}`}>{value}</dd>
    </div>
  );
}
